/**
 * BatchActionsSettingsSection — configure the Order Queue's batch buttons (BLU-777).
 *
 * Each button is `{ name, rule }` where the rule is either a SET OF SHOPIFY TAGS
 * or one of two builtin selectors. They render as checkboxes in the queue's
 * `Create Batch (N) ▾` menu; ticking several batches the deduped union.
 *
 * ⚠ A RULE HOLDS A LIST OF TAGS, NOT ONE TAG. Brett, 2026-08-12: "I would allow
 * adding two tags to 1 rule, or as many tags as you want to 1 rule in the UI."
 * That is load-bearing, not a convenience: the same real-world concept is spelled
 * two ways in live data — `Express-Shipping` and the legacy `Express Shipping`
 * (220 orders, last used 2026-06-10). One button has to catch both.
 *
 * ⚠ TAGS ARE PICKED, NEVER TYPED. The picker reads the org's own live tag
 * vocabulary via `searchOrderTags` — the same source the queue's tag filter uses.
 * Matching is exact and case-sensitive (Postgres `&&`), so a hand-typed tag with
 * the wrong case would silently match nothing. Free text is offered only as a
 * fallback when a search returns nothing, never as the default path.
 *
 * Mirrors OrderQueueSettingsSection: same query/mutation shape, same embedded
 * save-button placement, inventory-app design language.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertTriangle,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Search,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import {
  BATCH_ACTIONS_MAX,
  getEffectiveBatchActions,
  EXPRESS_BATCH_ACTION_ID,
  BATCH_ACTION_MAX_NAME_LENGTH,
  BATCH_ACTION_MAX_TAGS,
  BATCH_ACTION_MAX_TAG_LENGTH,
  type BatchAction,
  type BatchActionBuiltinKey,
} from '@threadheads/shared';

interface BatchActionsSettingsSectionProps {
  /** When true, renders without Card wrapper for embedding in accordions */
  embedded?: boolean;
}

const BUILTIN_LABELS: Record<Exclude<BatchActionBuiltinKey, 'express'>, { label: string; help: string }> = {
  priority: {
    label: 'Priority orders',
    // Named to match the queue's own "Priority" row badge, and deliberately NOT
    // the same wording as the create-time "Include re-queued orders?" toggle —
    // that toggle reads one org, this scope reads every org sharing the floor.
    help: 'Orders that were pushed to the front of the queue, for any reason.',
  },
  resolved_oos: {
    label: 'Out-of-stock, now resolved',
    help: 'Orders that were blocked on stock and someone has since resolved.',
  },
};

/** Client-generated stable id. Ticked state and the resolver key on this, not on name. */
function newActionId(): string {
  return `ba_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

export function BatchActionsSettingsSection({
  embedded = false,
}: BatchActionsSettingsSectionProps) {
  const { t } = useTranslation('settings');
  const [actions, setActions] = useState<BatchAction[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  const settingsQuery = trpc.productionSettings.getBatchActions.useQuery();
  const updateMutation = trpc.productionSettings.updateBatchActions.useMutation({
    onSuccess: () => {
      toast.success('Batch buttons saved');
      setHasChanges(false);
      settingsQuery.refetch();
    },
    onError: (error) => toast.error(`Failed to save: ${error.message}`),
  });

  useEffect(() => {
    if (settingsQuery.data) {
      setActions(settingsQuery.data.actions);
      setHasChanges(false);
    }
  }, [settingsQuery.data]);

  const filterTags = settingsQuery.data?.filterTags ?? [];

  const mutate = (next: BatchAction[]) => {
    setActions(next);
    setHasChanges(true);
  };

  const addTagAction = () =>
    mutate([...actions, { id: newActionId(), name: '', rule: { type: 'tags', tags: [] } }]);

  const addBuiltinAction = (key: Exclude<BatchActionBuiltinKey, 'express'>) =>
    mutate([
      ...actions,
      { id: newActionId(), name: BUILTIN_LABELS[key].label, rule: { type: 'builtin', key } },
    ]);

  const removeAction = (id: string) => mutate(actions.filter((a) => a.id !== id));

  const renameAction = (id: string, name: string) =>
    mutate(actions.map((a) => (a.id === id ? { ...a, name } : a)));

  const setActionTags = (id: string, tags: string[]) =>
    mutate(
      actions.map((a) => (a.id === id && a.rule.type === 'tags' ? { ...a, rule: { type: 'tags', tags } } : a))
    );

  /**
   * One-click starter set, built from the org's OWN curated tag vocabulary
   * (`filter_tags`) — never from hardcoded Threadheads tags, which would render
   * as dead buttons for Thread Lab / Mini Zali / Maku (all of whom ship empty
   * `filter_tags`).
   *
   * Shipping speed tags are offered in Express settings instead, where they
   * configure the canonical system scope without creating another tag button.
   */
  const suggestions = useMemo(() => {
    if (filterTags.length === 0) return [];
    const remaining = new Set(filterTags);
    const built: BatchAction[] = [];

    // Express is already a canonical system scope. Its optional speed-tag
    // suggestion belongs in Express settings, never in custom batch actions.
    ['Startrack-Premium', 'Express-Shipping', 'Express Shipping'].forEach((tag) => remaining.delete(tag));

    for (const tag of remaining) {
      built.push({
        id: newActionId(),
        // Title-case the tag into something readable: `custom_product` → "Custom Product".
        name: tag
          .replace(/[-_]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .replace(/\b\w/g, (c) => c.toUpperCase())
          .slice(0, BATCH_ACTION_MAX_NAME_LENGTH),
        rule: { type: 'tags', tags: [tag] },
      });
    }
    return built.slice(0, BATCH_ACTIONS_MAX);
  }, [filterTags]);

  const applySuggestions = () => {
    // Never clobber existing buttons — append only what isn't already covered.
    const existingTags = new Set(
      actions.flatMap((a) => (a.rule.type === 'tags' ? a.rule.tags : []))
    );
    const fresh = suggestions.filter(
      (s) => s.rule.type === 'tags' && s.rule.tags.some((t) => !existingTags.has(t))
    );
    if (fresh.length === 0) {
      toast.info('Your tag filters are already covered by the buttons you have.');
      return;
    }
    mutate([...actions, ...fresh].slice(0, BATCH_ACTIONS_MAX));
  };

  // Save is blocked rather than silently dropping rows: the backend Zod schema
  // rejects a nameless button or an empty tag rule, and a save that quietly
  // discarded half the list would read as data loss.
  const invalid = actions.filter(
    (a) => a.name.trim().length === 0 || (a.rule.type === 'tags' && a.rule.tags.length === 0)
  );
  const canSave = hasChanges && invalid.length === 0 && !updateMutation.isPending;

  const handleSave = () => updateMutation.mutate({ actions });

  const isLoading = settingsQuery.isLoading;
  const hasError = settingsQuery.isError;
  const atCap = actions.length >= BATCH_ACTIONS_MAX;

  const saveButton = hasChanges ? (
    <Button onClick={handleSave} disabled={!canSave} className="h-14 gap-2">
      {updateMutation.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Save className="h-4 w-4" />
      )}
      Save Changes
    </Button>
  ) : null;

  const content = (
    <div className="space-y-6">
      {hasError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load batch buttons</AlertTitle>
          <AlertDescription className="flex items-center gap-2">
            {settingsQuery.error?.message || 'Unknown error'}
            <Button variant="link" className="p-0 h-auto" onClick={() => settingsQuery.refetch()}>
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading batch buttons...</span>
        </div>
      )}

      {!isLoading && !hasError && (
        <>
          <div className="space-y-2">
            <Label className="text-base font-medium">Batch Buttons</Label>
            <p className="text-sm text-muted-foreground">
              Each button batches a whole group of orders in one press. They appear as tick boxes
              in the <span className="font-medium">Create Batch</span> menu on the Orders page.
              Tick several and the batch takes each order once, even if it is in two groups. The
              batch takes the oldest 200 orders at most.
            </p>
          </div>

          {getEffectiveBatchActions(settingsQuery.data?.actions)
            .filter((action) => action.id === settingsQuery.data?.systemAction?.id && action.id === EXPRESS_BATCH_ACTION_ID)
            .map((action) => (
              <div key={action.id} className="rounded-lg border bg-muted/30 p-4 space-y-2">
                <Badge variant="secondary">{t('express.scopeLabel')}</Badge>
                <p className="text-sm text-muted-foreground">{t('express.systemScopeHelp')}</p>
                <Button variant="link" asChild className="h-8 p-0">
                  <Link to="/production/settings?area=orders#express">{t('express.editDefinition')}</Link>
                </Button>
              </div>
            ))}

          {actions.length === 0 && (
            <Alert>
              <AlertDescription className="text-pretty">
                {t('express.noCustomScopes')}
              </AlertDescription>
            </Alert>
          )}

          {/* Configured buttons */}
          <div className="space-y-4">
            {actions.map((action) => (
              <div key={action.id} className="rounded-lg border bg-card p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="flex-1 space-y-1.5">
                    <Label htmlFor={`ba-name-${action.id}`} className="text-sm">
                      Button name
                    </Label>
                    <Input
                      id={`ba-name-${action.id}`}
                      value={action.name}
                      maxLength={BATCH_ACTION_MAX_NAME_LENGTH}
                      placeholder={t('express.customNamePlaceholder')}
                      onChange={(e) => renameAction(action.id, e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-7 text-muted-foreground hover:text-destructive"
                    onClick={() => removeAction(action.id)}
                    aria-label={`Remove ${action.name || 'button'}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {action.rule.type === 'builtin' ? (
                  <div className="space-y-1.5">
                    <Label className="text-sm">Batches</Label>
                    <Badge variant="secondary" className="text-sm font-normal">
                      {action.rule.key === 'express' ? t('express.scopeLabel') : BUILTIN_LABELS[action.rule.key].label}
                    </Badge>
                    <p className="text-xs text-muted-foreground">
                      {action.rule.key === 'express' ? t('express.systemScopeHelp') : BUILTIN_LABELS[action.rule.key].help}
                    </p>
                  </div>
                ) : (
                  <TagRuleEditor
                    actionId={action.id}
                    tags={action.rule.tags}
                    onChange={(tags) => setActionTags(action.id, tags)}
                  />
                )}

                {action.name.trim().length === 0 && (
                  <p className="text-xs text-destructive">Give this button a name.</p>
                )}
                {action.rule.type === 'tags' && action.rule.tags.length === 0 && (
                  <p className="text-xs text-destructive">
                    Pick at least one tag, or this button would do nothing.
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Add controls */}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={addTagAction} disabled={atCap} className="gap-2 h-11">
              <Plus className="h-4 w-4" />
              Add a tag button
            </Button>

            <Select
              value=""
              onValueChange={(value) => addBuiltinAction(value as Exclude<BatchActionBuiltinKey, 'express'>)}
              disabled={atCap}
            >
              <SelectTrigger className="w-full sm:w-[260px] h-11">
                <SelectValue placeholder="Add a built-in button" />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(BUILTIN_LABELS) as Exclude<BatchActionBuiltinKey, 'express'>[]).map((key) => (
                  <SelectItem key={key} value={key} className="py-3">
                    {BUILTIN_LABELS[key].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {suggestions.length > 0 && (
              <Button variant="outline" onClick={applySuggestions} disabled={atCap} className="gap-2 h-11">
                <Sparkles className="h-4 w-4" />
                Start from my tag filters
              </Button>
            )}
          </div>

          {atCap && (
            <p className="text-xs text-muted-foreground">
              {BATCH_ACTIONS_MAX} buttons is the maximum: remove one to add another.
            </p>
          )}
        </>
      )}

      <div role="status" aria-live="polite" className="sr-only">
        {updateMutation.isSuccess && 'Batch buttons saved successfully'}
        {hasChanges && invalid.length === 0 && 'You have unsaved changes'}
        {invalid.length > 0 && 'Some buttons are incomplete and cannot be saved yet'}
      </div>
    </div>
  );

  if (embedded) {
    return (
      <div className="space-y-4">
        {saveButton && <div className="flex justify-end">{saveButton}</div>}
        {content}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {content}
      {saveButton && <div className="flex justify-end">{saveButton}</div>}
    </div>
  );
}

/**
 * Tag picker for one rule. Reads the org's real tag vocabulary
 * (`searchOrderTags`) so the operator PICKS rather than types. For Batch
 * buttons matching is exact and case-sensitive, so a typo silently matches
 * nothing. Shared with DTF orders (BLU-1135), whose matching ignores case and
 * outer spaces; a caller with a different rule states it in `help` (shown
 * under the label and announced with the picker) and passes `caseInsensitive`
 * so a tag differing only in case or outer spaces counts as already added.
 */
export function TagRuleEditor({
  actionId,
  tags,
  onChange,
  help,
  caseInsensitive = false,
}: {
  actionId: string;
  tags: string[];
  onChange: (tags: string[]) => void;
  help?: string;
  caseInsensitive?: boolean;
}) {
  const { t } = useTranslation('settings');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebounced(query), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const results = trpc.productionSettings.searchOrderTags.useQuery(
    { query: debounced },
    { enabled: open && debounced.length > 0 }
  );

  const tagKey = (tag: string) => (caseInsensitive ? tag.trim().toLowerCase() : tag);
  const addedKeys = new Set(tags.map(tagKey));
  const isAdded = (tag: string) => addedKeys.has(tagKey(tag));
  const available = (results.data ?? []).filter((tag) => !isAdded(tag));
  const atCap = tags.length >= BATCH_ACTION_MAX_TAGS;
  const typed = debounced.trim();
  // Only offered when the search found nothing: a tag can exist on orders the
  // search index has not surfaced, and blocking entry outright would be worse
  // than allowing a deliberate manual add. Never longer than a tag can be saved.
  const canAddRaw =
    typed.length > 0 &&
    typed.length <= BATCH_ACTION_MAX_TAG_LENGTH &&
    available.length === 0 &&
    !results.isLoading &&
    !isAdded(typed);

  const pickerId = `tag-picker-${actionId}`;
  const helpId = `tag-picker-help-${actionId}`;
  const anyOfId = `ba-tags-help-${actionId}`;
  const describedBy = [help ? helpId : null, tags.length > 1 ? anyOfId : null].filter(Boolean).join(' ') || undefined;

  const addTag = (tag: string) => {
    if (atCap || isAdded(tag)) return;
    onChange([...tags, tag]);
    setQuery('');
    setDebounced('');
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm" htmlFor={pickerId}>{t('tagPicker.label')}</Label>
      {help && (
        <p className="text-xs text-muted-foreground" id={helpId}>
          {help}
        </p>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={pickerId}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-describedby={describedBy}
            disabled={atCap}
            className="w-full sm:w-[320px] justify-start gap-2 h-11 font-normal text-muted-foreground"
          >
            <Search className="h-4 w-4 shrink-0" />
            {atCap ? t('tagPicker.max', { count: BATCH_ACTION_MAX_TAGS }) : t('tagPicker.search')}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full sm:w-[320px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t('tagPicker.placeholder')}
              value={query}
              onValueChange={setQuery}
              maxLength={BATCH_ACTION_MAX_TAG_LENGTH}
            />
            <CommandList>
              {debounced.length === 0 ? (
                <div className="py-4 text-center text-sm text-muted-foreground">
                  {t('tagPicker.typePrompt')}
                </div>
              ) : results.isLoading ? (
                <div className="flex items-center justify-center py-4 gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{t('tagPicker.searching')}</span>
                </div>
              ) : available.length === 0 ? (
                <CommandEmpty>
                  <span className="block text-sm">{t('tagPicker.none')}</span>
                  {canAddRaw && (
                    <Button
                      variant="link"
                      className="mt-1 h-auto p-0 text-xs"
                      onClick={() => addTag(typed)}
                    >
                      {t('tagPicker.addAnyway', { tag: typed })}
                    </Button>
                  )}
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {available.map((tag) => (
                    <CommandItem
                      key={tag}
                      value={tag}
                      onSelect={() => addTag(tag)}
                      className="cursor-pointer"
                    >
                      {tag}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="max-w-full gap-1 whitespace-normal break-all pr-1 text-sm font-normal">
              {tag}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                onClick={() => onChange(tags.filter((existing) => existing !== tag))}
                aria-label={t('tagPicker.remove', { tag })}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      {tags.length > 1 && (
        <p className="text-xs text-muted-foreground" id={anyOfId}>
          {t('tagPicker.anyOf', { count: tags.length })}
        </p>
      )}
    </div>
  );
}
